'use client'

import { MarkdownViewer } from 'primitives/markdown-viewer'
import type { Project } from '../page-client'
import { ProjectPostMemberCard } from './project-post-member-card'
import { ProjectActions } from './project-actions'
import { ProjectCommentInput } from './project-comment-input'
import { ProjectComments } from './project-comments'
import { useProject } from '../_hooks/use-project-data'
import { AssetGallery } from '@web/app/[locale]/clubhouse/[slug]/(social)/discussions/[topicSlug]/[discussionSlug]/_components/asset-gallery'
import * as React from 'react'
import type { SocialProduct } from 'codegen/types'
import { useComments } from '../_hooks/use-project-comments'
import { useQueryClient } from '@tanstack/react-query'
import {
  socialGetPostQueryKey,
  socialDiscoveryFeedQueryRootKey,
  socialUserFeedQueryRootKey,
  socialFollowersFeedQueryRootKey,
  socialSavedFeedQueryRootKey,
} from '@web/gen/feed'
import type { SocialGetPostQuery } from '@web/gen/feed'
import type { InfiniteData } from '@tanstack/react-query'
import { DiscussionProductCard } from '@web/app/[locale]/clubhouse/[slug]/(social)/discussions/[topicSlug]/[discussionSlug]/_components/discussion-product-card'
import { Scroller } from 'primitives/scroller'
import { ProjectPostTutorialCard } from './project-post-tutorial-card'
import { useIsInViewport } from '../_hooks/use-is-in-viewport' // try to use the local one first to check performance
import { useFeedRoute } from '../contexts/feed-route-context'

const MAX_CONTENT_LENGTH = 500

// oxlint-disable-next-line complexity
export const ProjectPost = ({
  project,
  showImages = true,
  hideBookmark = false,
  showViewPostLink = true,
  size = 'regular',
  inPhotoViewMode = false,
  isTutorialPage = false,
}: {
  project: Project
  showImages?: boolean
  hideBookmark?: boolean
  showViewPostLink?: boolean
  size?: 'small' | 'regular'
  inPhotoViewMode?: boolean
  isTutorialPage?: boolean
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const isInViewport = useIsInViewport(containerRef, false, { once: true, rootMargin: '600px' })
  const isOptimisticPost = project.id.startsWith('optimistic-')

  const {
    project: projectClientData,
    // refetch,
    // isLoading,
  } = useProject({
    postSlug: project.postSlug || '',
    enabled: isInViewport && !isOptimisticPost,
  })

  // Use client data if available, otherwise fall back to initial project data
  const projectData = projectClientData || project
  const assetsKey = React.useMemo(() => {
    const a = projectData.assets ?? []
    return a
      .map(x => `${x.id}:${x.optimizedUrl ?? x.url ?? ''}:${x.thumbnailUrls?.length ?? 0}`)
      .join('|')
  }, [projectData.assets])

  const assetUrls = React.useMemo(
    () =>
      (projectData.assets ?? []).map(asset => ({
        id: asset.id,
        url: asset.type === 'video' ? (asset.videoInfo?.mp4_urls[0] ?? '') : (asset.url ?? ''),
        optimizedUrl: asset.type === 'video' ? asset.videoInfo?.mp4_urls[0] : asset.optimizedUrl,
        thumbnailUrls: asset.thumbnailUrls ?? null,
        type: asset.type,
        state: asset.videoInfo?.state ?? 'ready',
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetsKey],
  )

  const queryClient = useQueryClient()
  const commentsData = useComments(project.id)
  const [focusCounter, setFocusCounter] = React.useState(0)

  const updateFeedCommentsCount = React.useCallback(
    (delta: number) => {
      // Update socialGetPost cache (used when useProject has loaded)
      const queryKey = socialGetPostQueryKey({ input: { postSlug: project.postSlug || '' } })
      queryClient.setQueryData<SocialGetPostQuery>(queryKey, oldData => {
        if (!oldData?.socialGetPost?.post) {
          return oldData
        }
        return {
          ...oldData,
          socialGetPost: {
            ...oldData.socialGetPost,
            post: {
              ...oldData.socialGetPost.post,
              commentsCount: Math.max(0, (oldData.socialGetPost.post.commentsCount ?? 0) + delta),
            },
          },
        }
      })

      // Update feed infinite query caches (used when useProject hasn't loaded yet)
      const feedRootKeys = [
        socialDiscoveryFeedQueryRootKey,
        socialUserFeedQueryRootKey,
        socialFollowersFeedQueryRootKey,
        socialSavedFeedQueryRootKey,
      ]
      for (const rootKey of feedRootKeys) {
        const queries = queryClient.getQueryCache().findAll({
          queryKey: [rootKey],
        })
        for (const query of queries) {
          queryClient.setQueryData<
            InfiniteData<Record<string, { posts?: Array<{ id: string; commentsCount: number }> }>>
          >(query.queryKey, oldData => {
            if (!oldData?.pages) {
              return oldData
            }
            let changed = false
            const pages = oldData.pages.map(page => {
              const feedData = page[rootKey]
              if (!feedData?.posts) {
                return page
              }
              const idx = feedData.posts.findIndex(p => p.id === project.id)
              if (idx === -1) {
                return page
              }
              changed = true
              const posts = [...feedData.posts]
              posts[idx] = {
                ...posts[idx],
                commentsCount: Math.max(0, (posts[idx].commentsCount ?? 0) + delta),
              }
              return { ...page, [rootKey]: { ...feedData, posts } }
            })
            return changed ? { ...oldData, pages } : oldData
          })
        }
      }
    },
    [queryClient, project.postSlug, project.id],
  )

  const bumpCommentsCount = React.useCallback(
    () => updateFeedCommentsCount(1),
    [updateFeedCommentsCount],
  )
  const decrementCommentsCount = React.useCallback(
    () => updateFeedCommentsCount(-1),
    [updateFeedCommentsCount],
  )

  return (
    <div ref={containerRef} data-testid={`project-post-${project.id}`}>
      <ProjectPostMemberCard
        user={project.user}
        project={project}
        showViewPostLink={showViewPostLink}
        inPhotoViewMode={inPhotoViewMode}
      />
      <ShowMoreMarkdown
        content={projectData.content}
        className="mt-3"
        isProjectDetailPage={showViewPostLink}
        slug={project.postSlug || ''}
      />
      {projectData.assets && projectData.assets.length > 0 && showImages ? (
        <div className="mt-3">
          <AssetGallery assetUrls={assetUrls} postSlug={project.postSlug || ''} />
        </div>
      ) : null}
      <div className="mt-3">
        {projectData.socialProducts && projectData.socialProducts.length === 1 ? (
          <DiscussionProductCard
            product={projectData.socialProducts[0] as SocialProduct}
            size={size}
          />
        ) : projectData?.socialProducts ? (
          <Scroller.Root>
            <Scroller.ScrollArea
              gutter={0}
              gap={12}
              fadeOutFromBgColor="from-cf-white"
              className="mt-3"
            >
              {projectData?.socialProducts?.map(product => (
                <div key={product.id} className={'flex-none basis-5/6 '}>
                  <DiscussionProductCard product={product as SocialProduct} />
                </div>
              ))}
            </Scroller.ScrollArea>
          </Scroller.Root>
        ) : null}
      </div>
      {project.instructionItems?.length && showViewPostLink ? (
        <ProjectPostTutorialCard project={project} />
      ) : null}
      <div className="w-ful h-px border-t border-cf-transparent-grey-1000-8 mt-3" />
      <ProjectActions
        project={projectData}
        hideBookmark={hideBookmark}
        onClick={() => setFocusCounter(prev => prev + 1)}
      />
      {!isTutorialPage ? (
        <>
          <div className="mt-6">
            <ProjectCommentInput
              projectData={projectData}
              open={true}
              focus={focusCounter}
              onSubmit={newComment => {
                commentsData.handleNewComment(newComment)
                bumpCommentsCount()
              }}
            />
          </div>
          <div className="mt-3" data-testid="project-comments-container">
            <ProjectComments
              project={projectData}
              commentsData={commentsData}
              onBumpCommentsCount={bumpCommentsCount}
              onDecrementCommentsCount={decrementCommentsCount}
            />
          </div>
        </>
      ) : null}
    </div>
  )
}

export function ShowMoreMarkdown({
  content,
  className,
  isProjectDetailPage,
  slug,
}: {
  content: string
  className?: string
  isProjectDetailPage?: boolean
  slug?: string
}) {
  const [expanded, setExpanded] = React.useState(false)
  const isLong = (content?.length ?? 0) > MAX_CONTENT_LENGTH

  const truncated = React.useMemo(() => {
    if (!isLong) {
      return content
    }
    let cut = content.slice(0, MAX_CONTENT_LENGTH).replace(/\s+\S*$/, '')

    const fenceCount = (cut.match(/```/g) || []).length
    if (fenceCount % 2 === 1) {
      cut += '\n```'
    }
    const inlineTickCount = (cut.match(/(?<!`)`(?!`)/g) || []).length
    if (inlineTickCount % 2 === 1) {
      cut += '`'
    }

    return cut + '…'
  }, [content, isLong])

  const toRender = expanded || !isLong ? content : truncated

  const { basePath } = useFeedRoute()
  const redirectUrlBase =
    !isProjectDetailPage && slug
      ? `${basePath}/${slug}/redirect?href=`
      : `${basePath}/redirect?href=`

  return (
    <div className={className}>
      <div className="relative">
        <div
          className="text-16 text-cf-grey-800 mt-3 leading-[24px] break-words"
          data-testid="show-more-markdown-content"
        >
          <MarkdownViewer redirectUrlBase={redirectUrlBase} enableHttpsLinkDetection>
            {toRender}
          </MarkdownViewer>
        </div>
      </div>

      {isLong ? (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-0.5 text-14 text-cf-purple-900 hover:underline"
          aria-expanded={expanded}
          data-testid="show-more-markdown-toggle"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  )
}
